// =============================================================================
// CorpusCanvas: the read+drag card canvas for the Refine tab (brief B-3.a/b).
// Renders the user's corpus grouped by role/era (corpusGrouping.ts, mirroring
// apps/web's groupByRole/sortedRoles) and wires the native-drag rehome
// (dragRehome.ts + useCorpusCanvas's rehome()) across RoleGroupSections.
//
// Empty/loading/error states say only what is actually true (brief law d):
// no role yet -> "import your resume or talk to the agent" (the two REAL
// on-ramps this tab offers), never an invented count or a generic "get
// started" line.
//
// WIRING TARGET #1 (résumé display) — additive props thread the per-era
// "read as résumé" toggle (resumeEras, owned by the parent so it survives a
// canvas re-render) and the servedBulletsByRole map (sliced from the corpus-
// résumé document by the parent, same join apps/web's Corpus.tsx does) down
// to each RoleGroupSection. Both are optional and default to "off" — a caller
// that doesn't pass them gets byte-identical behaviour to before this brief.
//
// LANE ROLE-REFINE — additive props thread role field editing (onSaveRoleEdit/
// savingRoleId) and achievement field editing (onSaveAchievementEdit/
// savingAchievementId) down to every RoleGroupSection, including the orphan
// "Unattached" bucket for achievement editing (a role-less card is still
// editable — only role editing itself needs a real role row to PATCH).
// =============================================================================

import type { ResumeRoleBullet } from '@careeropt/schema';
import { useState } from 'react';
import type { AchievementPatch, AchievementRecord, RolePatch, RoleRecord } from '../lib/api';
import { activeRoles, groupAchievementsByRole, orderRolesForCanvas } from '../lib/corpusGrouping';
import { isRehomePending } from '../lib/dragRehome';
import type { DragRehomeState } from '../lib/dragRehome';
import { RoleGroupSection } from './RoleGroupSection';
import styles from './CorpusCanvas.module.css';

export interface CorpusCanvasProps {
  roles: RoleRecord[];
  achievements: AchievementRecord[];
  loading: boolean;
  error: string | null;
  dragState: DragRehomeState;
  onRehome: (cardId: string, targetRoleId: string | null) => void;
  /** Era ids currently toggled to read AS a résumé fragment. Omitted/empty ->
   *  every era opens on the card grid (pre-existing behaviour). */
  resumeEras?: Set<string>;
  onToggleResumeEra?: (roleId: string) => void;
  /** roleId -> the SERVED ghostwritten bullets for that era, sliced from the
   *  corpus-résumé document by the parent. */
  servedBulletsByRole?: Map<string, ResumeRoleBullet[]>;
  /** Present -> every era's pencil button renders and saves through this.
   *  Absent -> role editing is off, byte-identical to before this lane. */
  onSaveRoleEdit?: (roleId: string, patch: RolePatch) => Promise<string | null>;
  /** The role id currently mid-save (that era's form shows the saving state). */
  savingRoleId?: string | null;
  /** Present -> every achievement card's pencil button renders and saves
   *  through this. Absent -> achievement editing is off, byte-identical to
   *  before this lane. */
  onSaveAchievementEdit?: (achievementId: string, patch: AchievementPatch) => Promise<string | null>;
  /** The achievement id currently mid-save (that card shows the saving state). */
  savingAchievementId?: string | null;
}

export function CorpusCanvas({
  roles,
  achievements,
  loading,
  error,
  dragState,
  onRehome,
  resumeEras,
  onToggleResumeEra,
  servedBulletsByRole,
  onSaveRoleEdit,
  savingRoleId = null,
  onSaveAchievementEdit,
  savingAchievementId = null,
}: CorpusCanvasProps) {
  const [draggingCardId, setDraggingCardId] = useState<string | null>(null);
  const [dragOverRoleId, setDragOverRoleId] = useState<string | null>(null); // null-role bucket uses '__unattached__'

  if (loading) {
    return <div className={styles.status}>Loading your story…</div>;
  }

  if (error) {
    return (
      <div className={styles.status} role="alert">
        Couldn't load your story: {error}
      </div>
    );
  }

  if (roles.length === 0 && achievements.length === 0) {
    return (
      <div className={styles.empty}>
        <div className={styles.emptyTitle}>No roles yet</div>
        <div className={styles.emptyBody}>
          Import your resume above, or talk to Meira — either way, your stories land here as cards.
        </div>
      </div>
    );
  }

  // W3: the live canvas shows only NON-archived roles + cards. A user-archived
  // role/card (removed a bad import) lives in the collapsed archived section
  // (CorpusArchivePanel) instead — never as a live card.
  const liveRoles = activeRoles(roles);
  const groups = groupAchievementsByRole(liveRoles, achievements);
  const orderedRoles = orderRolesForCanvas(liveRoles);
  const pending = isRehomePending(dragState);

  return (
    <div className={styles.canvas}>
      {orderedRoles.map((role) => {
        const items = groups.find((g) => g.role?.id === role.id)?.items ?? [];
        return (
          <RoleGroupSection
            key={role.id}
            role={role}
            items={items}
            draggingCardId={draggingCardId}
            pendingCardId={dragState.pendingCardId}
            isDropTarget={dragOverRoleId === role.id}
            onDragStartCard={setDraggingCardId}
            onDragEndCard={() => {
              setDraggingCardId(null);
              setDragOverRoleId(null);
            }}
            onDragOverSection={() => {
              if (!pending) setDragOverRoleId(role.id);
            }}
            onDragLeaveSection={() => setDragOverRoleId((cur) => (cur === role.id ? null : cur))}
            onDropCard={(cardId, targetRoleId) => {
              setDraggingCardId(null);
              setDragOverRoleId(null);
              if (!pending) onRehome(cardId, targetRoleId);
            }}
            asResume={resumeEras?.has(role.id) ?? false}
            onToggleResume={onToggleResumeEra ? () => onToggleResumeEra(role.id) : undefined}
            servedBullets={servedBulletsByRole?.get(role.id)}
            onSaveRoleEdit={onSaveRoleEdit}
            savingRoleEdit={savingRoleId === role.id}
            onSaveAchievementEdit={onSaveAchievementEdit}
            savingAchievementId={savingAchievementId}
          />
        );
      })}

      {(() => {
        const orphan = groups.find((g) => g.role === null);
        if (!orphan || orphan.items.length === 0) return null;
        return (
          <RoleGroupSection
            key="unattached"
            role={null}
            items={orphan.items}
            draggingCardId={draggingCardId}
            pendingCardId={dragState.pendingCardId}
            isDropTarget={dragOverRoleId === '__unattached__'}
            onDragStartCard={setDraggingCardId}
            onDragEndCard={() => {
              setDraggingCardId(null);
              setDragOverRoleId(null);
            }}
            onDragOverSection={() => {
              if (!pending) setDragOverRoleId('__unattached__');
            }}
            onDragLeaveSection={() =>
              setDragOverRoleId((cur) => (cur === '__unattached__' ? null : cur))
            }
            onDropCard={(cardId, targetRoleId) => {
              setDraggingCardId(null);
              setDragOverRoleId(null);
              if (!pending) onRehome(cardId, targetRoleId);
            }}
            onSaveAchievementEdit={onSaveAchievementEdit}
            savingAchievementId={savingAchievementId}
          />
        );
      })()}
    </div>
  );
}
